import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Card,
  Typography,
  Select,
  Tag,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';

const { Title } = Typography;
const { TextArea } = Input;

interface GameType {
  id: number;
  name: string;
  display_name: string;
}

interface GameParameterComment {
  id: number;
  game_type_id: number;
  game_name: string;
  game_display_name: string;
  parameter_key: string;
  parameter_comment: string | null;
  parameter_description: string | null;
  config_section: string;
  config_file: 'GameUserSettings' | 'GameIni';
  created_at: string;
  updated_at: string;
}

const GameParameterCommentsManagement: React.FC = () => {
  const [comments, setComments] = useState<GameParameterComment[]>([]);
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingComment, setEditingComment] = useState<GameParameterComment | null>(null);
  const [form] = Form.useForm();
  
  // 搜索和筛选状态
  const [searchText, setSearchText] = useState('');
  const [filterGameTypeId, setFilterGameTypeId] = useState<number | undefined>(undefined);
  const [filterConfigFile, setFilterConfigFile] = useState<string | undefined>(undefined);

  // 获取游戏类型列表
  const fetchGameTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/game-parameter-comments/game-types/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setGameTypes(data.data);
      } else {
        message.error(data.message || '获取游戏类型失败');
      }
    } catch (error) {
      console.error('获取游戏类型失败:', error);
      message.error('获取游戏类型失败');
    }
  };

  // 获取游戏参数注释列表
  const fetchComments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/game-parameter-comments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setComments(data.data);
      } else {
        message.error(data.message || '获取游戏参数注释失败');
      }
    } catch (error) {
      console.error('获取游戏参数注释失败:', error);
      message.error('获取游戏参数注释失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存游戏参数注释
  const handleSave = async (values: any) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingComment 
        ? `/api/admin/game-parameter-comments/${editingComment.id}`
        : '/api/admin/game-parameter-comments';
      
      const method = editingComment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      if (data.success) {
        message.success(editingComment ? '游戏参数注释更新成功' : '游戏参数注释创建成功');
        setModalVisible(false);
        setEditingComment(null);
        form.resetFields();
        fetchComments();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存游戏参数注释失败:', error);
      message.error('保存游戏参数注释失败');
    }
  };

  // 删除游戏参数注释
  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/game-parameter-comments/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        message.success('游戏参数注释删除成功');
        fetchComments();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除游戏参数注释失败:', error);
      message.error('删除游戏参数注释失败');
    }
  };

  // 编辑游戏参数注释
  const handleEdit = (comment: GameParameterComment) => {
    setEditingComment(comment);
    form.setFieldsValue({
      game_type_id: comment.game_type_id,
      parameter_key: comment.parameter_key,
      parameter_comment: comment.parameter_comment,
      parameter_description: comment.parameter_description,
      config_section: comment.config_section,
      config_file: comment.config_file
    });
    setModalVisible(true);
  };

  // 添加游戏参数注释
  const handleAdd = () => {
    setEditingComment(null);
    form.resetFields();
    setModalVisible(true);
  };

  useEffect(() => {
    fetchGameTypes();
    fetchComments();
  }, []);

  // 过滤后的数据
  const filteredComments = useMemo(() => {
    return comments.filter(comment => {
      // 搜索文本过滤（搜索游戏名称、参数键、配置节、参数注释、参数描述）
      const matchesSearch = !searchText || 
        (comment.game_name?.toLowerCase().includes(searchText.toLowerCase())) ||
        (comment.game_display_name?.toLowerCase().includes(searchText.toLowerCase())) ||
        (comment.parameter_key?.toLowerCase().includes(searchText.toLowerCase())) ||
        (comment.config_section?.toLowerCase().includes(searchText.toLowerCase())) ||
        (comment.parameter_comment?.toLowerCase().includes(searchText.toLowerCase())) ||
        (comment.parameter_description?.toLowerCase().includes(searchText.toLowerCase()));
      
      // 游戏类型过滤
      const matchesGameType = !filterGameTypeId || comment.game_type_id === filterGameTypeId;
      
      // 配置文件过滤
      const matchesConfigFile = !filterConfigFile || comment.config_file === filterConfigFile;
      
      return matchesSearch && matchesGameType && matchesConfigFile;
    });
  }, [comments, searchText, filterGameTypeId, filterConfigFile]);

  // 清空筛选
  const handleClearFilters = () => {
    setSearchText('');
    setFilterGameTypeId(undefined);
    setFilterConfigFile(undefined);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left' as const,
    },
    {
      title: '游戏类型',
      key: 'game',
      width: 150,
      render: (_: any, record: GameParameterComment) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.game_display_name || record.game_name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.game_name}</div>
        </div>
      ),
    },
    {
      title: '配置文件',
      dataIndex: 'config_file',
      key: 'config_file',
      width: 120,
      render: (value: string) => (
        <Tag color={value === 'GameUserSettings' ? 'blue' : 'green'}>
          {value}
        </Tag>
      ),
    },
    {
      title: '配置节',
      dataIndex: 'config_section',
      key: 'config_section',
      width: 150,
    },
    {
      title: '参数键',
      dataIndex: 'parameter_key',
      key: 'parameter_key',
      width: 200,
      ellipsis: true,
    },
    {
      title: '参数注释',
      dataIndex: 'parameter_comment',
      key: 'parameter_comment',
      width: 200,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '参数描述',
      dataIndex: 'parameter_description',
      key: 'parameter_description',
      width: 250,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: GameParameterComment) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个游戏参数注释吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>游戏参数注释管理</Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchComments}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加参数注释
            </Button>
          </Space>
        </div>

        {/* 搜索和筛选区域 */}
        <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Input
                placeholder="搜索游戏名称、参数键、配置节、注释、描述..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Select
                placeholder="筛选游戏类型"
                style={{ width: '100%' }}
                value={filterGameTypeId}
                onChange={(value) => setFilterGameTypeId(value)}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {gameTypes.map(game => (
                  <Select.Option key={game.id} value={game.id}>
                    {game.display_name} ({game.name})
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Select
                placeholder="筛选配置文件"
                style={{ width: '100%' }}
                value={filterConfigFile}
                onChange={(value) => setFilterConfigFile(value)}
                allowClear
              >
                <Select.Option value="GameUserSettings">GameUserSettings</Select.Option>
                <Select.Option value="GameIni">GameIni</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={4} lg={4}>
              <Button
                style={{ width: '100%' }}
                onClick={handleClearFilters}
                disabled={!searchText && !filterGameTypeId && !filterConfigFile}
              >
                清空筛选
              </Button>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={filteredComments}
          scroll={{ x: 1500 }}
          rowKey="id"
          loading={loading}
          className="admin-game-parameter-comments-table"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => {
              const filteredTotal = filteredComments.length;
              const allTotal = comments.length;
              if (filteredTotal === allTotal) {
                return `共 ${total} 条记录`;
              }
              return `显示 ${range[0]}-${range[1]} 条，共 ${filteredTotal} 条（全部 ${allTotal} 条）`;
            },
          }}
        />
      </Card>

      <Modal
        title={editingComment ? '编辑游戏参数注释' : '添加游戏参数注释'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingComment(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="game_type_id"
            label="游戏类型"
            rules={[{ required: true, message: '请选择游戏类型' }]}
          >
            <Select placeholder="请选择游戏类型" showSearch optionFilterProp="children">
              {gameTypes.map(game => (
                <Select.Option key={game.id} value={game.id}>
                  {game.display_name} ({game.name})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="config_file"
            label="配置文件"
            rules={[{ required: true, message: '请选择配置文件' }]}
          >
            <Select placeholder="请选择配置文件">
              <Select.Option value="GameUserSettings">GameUserSettings</Select.Option>
              <Select.Option value="GameIni">GameIni</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="config_section"
            label="配置节"
            rules={[{ required: true, message: '请输入配置节' }]}
          >
            <Input placeholder="例如: [/Script/ShooterGame.ShooterGameUserSettings]" />
          </Form.Item>

          <Form.Item
            name="parameter_key"
            label="参数键"
            rules={[{ required: true, message: '请输入参数键' }]}
          >
            <Input placeholder="例如: ServerAdminPassword" />
          </Form.Item>

          <Form.Item
            name="parameter_comment"
            label="参数注释（翻译）"
          >
            <Input placeholder="例如: 服务器管理员密码" maxLength={500} />
          </Form.Item>

          <Form.Item
            name="parameter_description"
            label="参数描述"
          >
            <TextArea
              placeholder="请输入参数的详细描述"
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GameParameterCommentsManagement;

